import { useEffect, useRef, useState, ReactNode } from "react";
import { Arrow } from "react-laag";
import { useTooltip } from "../hooks/useTooltip";

import "../../styles/fuzzy/fuzzyThreshold.css";
import { Button } from "../buttons/Button";
import { RangeInput } from "../inputs/Range";

export { SliderButton };

// TODO: where is setting state when component unmounted ??
type Props = {
  /**
   * Range labbel
   */
  label: string;
  threshold: number;
  update(threshold: number): void;
  /**
   * Child node of the always visible button
   */
  buttonChildren?: ReactNode;
  /** Overrides buttonChildren */
  buttonChildrenFn?: (value: number) => ReactNode;
  buttonClassName?: string;
  rangeInputChildren?: (
    value: number,
    onMove: Props["update"],
    onChange: Props["update"],
  ) => ReactNode;
  rangeInputClassName?: string;
};
const SliderButton = (props: Props) => {
  if (!props.buttonChildren && !props.buttonChildrenFn) {
    throw new Error("Cannot create SliderButton with no button children");
  }
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [slidingValue, setThreshold] = useState<number>(props.threshold);

  const {
    renderLayer,
    triggerProps,
    layerProps,
    arrowProps,
    layerSide,
    setOpen,
    isOpen,
  } = useTooltip(
    // reattach to button
    buttonRef,
    // outside click
    () => {
      setOpen(false);
      props.update(slidingValue);
    },
    // on disappear
    (disappearType) => {
      if (disappearType == "full") {
        setOpen(false);
        props.update(slidingValue);
      }
    },
    // parent close
    () => {
      setOpen(false);
      props.update(slidingValue);
    },
  );

  // update state threshold when props change
  useEffect(() => {
    setThreshold(props.threshold);
  }, [props.threshold]);

  // TODO: useCallback and useState ?? https://medium.com/welldone-software/usecallback-might-be-what-you-meant-by-useref-useeffect-773bc0278ae
  useEffect(() => {
    // call react-laag triggerRef, otherwise error !
    triggerProps?.ref(buttonRef.current);
  }, [buttonRef, buttonRef?.current]);

  return (
    <>
      <Button
        {...triggerProps}
        ref={buttonRef}
        onClick={() => setOpen(!isOpen)}
        className={props.buttonClassName}
      >
        {!props.buttonChildrenFn && props.buttonChildren}
        {props.buttonChildrenFn && props.buttonChildrenFn(props.threshold)}
      </Button>
      {isOpen &&
        renderLayer(
          <div
            {...layerProps}
            style={{
              ...layerProps.style,
              // add merge properties here
            }}
            className={"tooltip " + layerSide}
          >
            {/* Show custom RangeInput */}
            {props.rangeInputChildren &&
              props.rangeInputChildren(
                slidingValue,
                setThreshold,
                (newThreshold: number) => {
                  setThreshold(newThreshold);
                  setOpen(false);
                  props.update(newThreshold);
                },
              )}
            {/* Show default threshold  */}
            {!props.rangeInputChildren && (
              <RangeInput
                {...props}
                value={slidingValue}
                inputLabel
                className={props.rangeInputClassName}
                max={100}
                min={50}
                onChange={(newThreshold) => {
                  setThreshold(newThreshold);
                  setOpen(false);
                  props.update(newThreshold);
                }}
                onMove={setThreshold}
              />
            )}
            <Arrow
              className={"arrow " + layerSide}
              {...arrowProps}
              size={5}
              roundness={0}
            />
          </div>,
        )}
    </>
  );
};
